// Date formatting utilities — always display in Admin-configured app timezone.
import localeConfig from '../config/locale.config';

let runtimeLocale = localeConfig.intlLocale || 'en-NZ';
let runtimeTimezone = localeConfig.timezone || 'Pacific/Auckland';

/** Apply market defaults from payments/config or /api/common/localization. */
export function setLocaleDefaults({ locale, timezone } = {}) {
    if (locale) runtimeLocale = String(locale);
    if (timezone) runtimeTimezone = String(timezone);
}

export function getAppTimezone() {
    return runtimeTimezone;
}

export function getAppLocale() {
    return runtimeLocale;
}

/** True when the value carries an explicit offset or Z (absolute instant). */
function isTimezoneAware(value) {
    if (value instanceof Date) return true;
    const s = String(value || '').trim();
    return /([zZ]|[+-]\d{2}:?\d{2})$/.test(s);
}

/**
 * Format options: for timezone-aware values, convert into app TZ;
 * for naive DB wall-clock strings, preserve digits (already stored in app TZ).
 */
function formatWithOptions(date, options) {
    if (!date) return 'N/A';
    try {
        const opts = isTimezoneAware(date)
            ? { ...options, timeZone: runtimeTimezone }
            : { ...options };
        return new Date(date).toLocaleString(runtimeLocale, opts);
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'N/A';
    }
}

/**
 * Format date using app locale (en-NZ → dd/mm/yyyy) in configured timezone.
 */
export const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
        const options = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        };
        if (isTimezoneAware(date)) options.timeZone = runtimeTimezone;
        return new Date(date).toLocaleDateString(runtimeLocale, options);
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'N/A';
    }
};

/**
 * Format date and time using app locale in configured timezone.
 */
export const formatDateTime = (date) => {
    return formatWithOptions(date, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * Format date for chart display (short month and day).
 */
export const formatDateForChart = (date) => {
    if (!date) return 'N/A';
    try {
        const options = {
            month: 'short',
            day: 'numeric',
        };
        if (isTimezoneAware(date)) options.timeZone = runtimeTimezone;
        return new Date(date).toLocaleDateString(runtimeLocale, options);
    } catch (error) {
        console.error('Error formatting date for chart:', error);
        return 'N/A';
    }
};

/**
 * Format date for datetime-local inputs (YYYY-MM-DDTHH:MM) in app timezone.
 */
export const formatDateTimeForInput = (date) => {
    if (!date) return '';
    try {
        if (!(date instanceof Date) && !isTimezoneAware(date)) {
            const normalized = String(date).trim().replace(' ', 'T');
            return normalized.slice(0, 16);
        }
        const d = date instanceof Date ? date : new Date(date);
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: runtimeTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).formatToParts(d);
        const get = (type) => parts.find((p) => p.type === type)?.value || '';
        return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    } catch (error) {
        console.error('Error formatting date for input:', error);
        return '';
    }
};

/**
 * Format date for file download names (YYYY-MM-DD in app timezone).
 */
export const formatDateForFileName = (date) => {
    if (!date) return '';
    try {
        const d = date instanceof Date ? date : new Date(date);
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: runtimeTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(d);
        const get = (type) => parts.find((p) => p.type === type)?.value || '';
        return `${get('year')}-${get('month')}-${get('day')}`;
    } catch (error) {
        console.error('Error formatting date for file name:', error);
        return '';
    }
};
